import React, { useState, useEffect } from "react";
import { LockOpenIcon, ShoppingCartIcon } from "lucide-react";
import { Button } from "~/components/ui/button";
import { CheckoutForm } from "~/modules/billing/components/CheckoutForm";
import { Link } from "~/components/Link";
import { LoadingSpinner } from "~/components/LoadingSpinner";
import { useSidebar } from "~/components/SidebarLayout/SidebarContext";
import { Routes } from "~/routes";
import { useWindowSize } from "~/utils/useWindowSize";
import { usePageScrollLock } from "~/hooks/use-page-scroll-lock";
import { useScrollThreshold } from "~/hooks/use-scroll-threshold";
import * as Analytics from "~/modules/dashboard_page_unlocks/analytics";
import { UNLOCK_PAGE_DRAWER_BENEFITS } from "~/modules/dashboard_page_unlocks/consts";
import {
    DrawerBackdrop,
    DrawerContent,
    DrawerFooter,
    DrawerFooterItem,
    DrawerHeader,
    DrawerSheet,
    DrawerSubheader,
} from "~/components/Drawer";

// // // //

/**
 * UnlockPageDrawer
 * Bottom drawer paywall shown on dashboard pages to users without a paid plan.
 * Mirrors the `PaywallDrawer` look and behavior (appears after scrolling 30% of
 * the page), with an additional
 * "Unlock this page" CTA that spends one of the organization's page unlocks.
 */
export function UnlockPageDrawer(props: {
    organizationId: string;
    isFetching?: boolean;
    isUnlocking?: boolean;
    remainingUnlockCount?: number;
    hasLoadError?: boolean;
    errorMessage?: string | null;
    onClickUnlock?: () => void;
}) {
    const {
        organizationId,
        isFetching = false,
        isUnlocking = false,
        remainingUnlockCount,
        hasLoadError = false,
        errorMessage = null,
        onClickUnlock,
    } = props;
    const [isVisible, setIsVisible] = useState(false);
    const { sidebarWidth } = useSidebar();

    const { isLg, isXl, is2xl } = useWindowSize();
    const isLargeScreen = isLg || isXl || is2xl;

    // Show the drawer after scrolling 30% down the page
    useScrollThreshold({
        thresholdPercent: 30,
        onThresholdCrossed: () => setIsVisible(true),
    });

    // Lock page scroll while the drawer is visible
    usePageScrollLock({ lockScroll: isVisible });

    // Track once when the gate is rendered - independent of the scroll threshold
    // NOTE - mount-only on purpose, so later prop changes don't re-fire the event
    useEffect(() => {
        Analytics.trackUnlockPageDrawerGateViewed({ remainingUnlockCount });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isVisible) {
            Analytics.trackUnlockPageDrawerShown({ remainingUnlockCount });
        }
    }, [isVisible, remainingUnlockCount]);

    if (!isVisible) return null;

    return (
        <UnlockPageDrawerView
            isLargeScreen={isLargeScreen}
            sidebarWidth={sidebarWidth}
            isFetching={isFetching}
            isUnlocking={isUnlocking}
            remainingUnlockCount={remainingUnlockCount}
            organizationId={organizationId}
            hasLoadError={hasLoadError}
            errorMessage={errorMessage}
            onClickUnlock={onClickUnlock}
        />
    );
}

// // // //

/**
 * DrawerError
 * Inline error callout shown above the drawer's CTAs
 */
function DrawerError(props: { children: React.ReactNode }) {
    return (
        <p
            role="alert"
            className="mx-auto mb-5 max-w-md rounded-md border border-red-500/20 bg-red-500/10 px-4 py-2.5 text-sm leading-relaxed text-red-700 dark:text-red-400 font-sans"
        >
            {props.children}
        </p>
    );
}

export function UnlockPageDrawerView(props: {
    organizationId: string;
    isLargeScreen: boolean;
    sidebarWidth: number;
    isFetching: boolean;
    isUnlocking: boolean;
    remainingUnlockCount?: number;
    /** The unlock list failed to load, so the unlock status + remaining count are unknown. Shows a reload prompt in place of the unlock button */
    hasLoadError?: boolean;
    /** The unlock itself failed. Shown above the CTAs, and the unlock button becomes "Try again" */
    errorMessage?: string | null;
    onClickUnlock?: () => void;
}) {
    const {
        organizationId,
        isLargeScreen,
        sidebarWidth,
        isFetching,
        isUnlocking,
        remainingUnlockCount,
        hasLoadError = false,
        errorMessage = null,
        onClickUnlock,
    } = props;

    const showUnlockButton = onClickUnlock !== undefined && !hasLoadError;

    return (
        <>
            <DrawerBackdrop />

            {/* Drawer - positioned to avoid sidebar on large screens */}
            <DrawerSheet offsetLeft={isLargeScreen ? sidebarWidth : 0}>
                <DrawerContent>
                    {/* Headline */}
                    <DrawerHeader>
                        Unlock full asset data with RWA.xyz Pro
                    </DrawerHeader>

                    {/* Subheading */}
                    <DrawerSubheader>
                        Get full access to market data, legal and regulatory
                        details, service providers, and transaction history for
                        every asset.
                    </DrawerSubheader>

                    <div className="flex flex-col justify-center">
                        {isFetching && (
                            <div
                                className="flex min-h-12 items-center justify-center"
                                aria-busy="true"
                                aria-label="Loading unlock status"
                            >
                                <LoadingSpinner size={8} />
                            </div>
                        )}

                        {!isFetching && (
                            <>
                                {/* Error */}
                                {hasLoadError && (
                                    <DrawerError>
                                        An error has occurred.{" "}
                                        <button
                                            type="button"
                                            onClick={() =>
                                                window.location.reload()
                                            }
                                            className="font-medium underline underline-offset-2 hover:no-underline"
                                        >
                                            Reload page
                                        </button>
                                    </DrawerError>
                                )}
                                {!hasLoadError && errorMessage && (
                                    <DrawerError>{errorMessage}</DrawerError>
                                )}

                                {/* CTA Buttons */}
                                <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                                    {showUnlockButton && (
                                        <Button
                                            type="button"
                                            disabled={isUnlocking}
                                            variant="primaryLight"
                                            size="lg"
                                            className="rounded-full text-base"
                                            onClick={() => {
                                                Analytics.trackUnlockPageDrawerUnlockClick(
                                                    { remainingUnlockCount },
                                                );
                                                onClickUnlock();
                                            }}
                                        >
                                            {isUnlocking ? (
                                                <LoadingSpinner size={4} />
                                            ) : (
                                                <LockOpenIcon />
                                            )}
                                            {errorMessage
                                                ? "Try again"
                                                : "Unlock this page"}
                                        </Button>
                                    )}
                                    <CheckoutForm
                                        text="Upgrade to Pro"
                                        organizationId={organizationId}
                                        enableAdditionalSeatsSelect={false}
                                        variant={
                                            showUnlockButton
                                                ? "outline"
                                                : "primaryLight"
                                        }
                                        size="lg"
                                        className="rounded-full text-base"
                                        icon={<ShoppingCartIcon />}
                                    />
                                </div>

                                {/* Fine print */}
                                <p className="text-sm text-muted-foreground mt-5 font-sans">
                                    {showUnlockButton &&
                                        remainingUnlockCount !== undefined && (
                                            <>
                                                {remainingUnlockCount}{" "}
                                                {remainingUnlockCount === 1
                                                    ? "page unlock"
                                                    : "page unlocks"}{" "}
                                                remaining
                                                <span aria-hidden="true">
                                                    {" · "}
                                                </span>
                                            </>
                                        )}
                                    <Link
                                        href={Routes.salesPage}
                                        analyticsLabel={
                                            Analytics
                                                .DashboardPageUnlocksUserEvents
                                                .UnlockPageDrawerViewPricingLinkClick
                                        }
                                        className="text-foreground underline hover:no-underline"
                                    >
                                        View pricing
                                    </Link>
                                </p>
                            </>
                        )}
                    </div>

                    {/* Benefits */}
                    <DrawerFooter>
                        {UNLOCK_PAGE_DRAWER_BENEFITS.map((benefit) => (
                            <DrawerFooterItem key={benefit} showCheckmark>
                                {benefit}
                            </DrawerFooterItem>
                        ))}
                    </DrawerFooter>
                </DrawerContent>
            </DrawerSheet>
        </>
    );
}
